import { useState, useEffect } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  cn,
  V2Loader,
  type CarouselApi,
} from "web-design-system";
import { Client, GiftFormDetails, GiftKycAmc } from "creso-core/types";
import { AppLogoLight } from "web-core";
import { useLatestGiftInfo } from "creso-core/hooks";
import { imageAssetUrl } from "@/utils/url";
import { GiftKycFormData, INITIAL_FORM_DATA } from "../../types";
import { ClientSelectStep } from "./steps/client-select-step";
import { DocumentsStep } from "./steps/documents-step";
import { KycOccupationStep } from "./steps/kyc-occupation-step";
import { ReviewSignStep } from "./steps/review-sign-step";
import { SuccessStep } from "./steps/success-step";

const giftStartImg = imageAssetUrl("gift/gift-start.png");
const giftFaangImg = imageAssetUrl("gift/gift-faang.png");
const giftDiverseImg = imageAssetUrl("gift/gift-diverse.png");
const giftLogoImg = imageAssetUrl("gift/gift.png");

const TOTAL_FORM_STEPS = 4;

/**
 * Form fields prefilled from the client's latest gift-city-form. Documents are
 * set to the previously uploaded file's URL rather than a File.
 */
function getPrefillFromDetails({
  data,
  pan_card_image,
  address_proof_image,
  cancelled_cheque_image,
}: GiftFormDetails): Partial<GiftKycFormData> {
  return {
    ckycNumber: data?.ckyc_number ?? "",
    fatherName: data?.father_name ?? "",
    occupationType: data?.occupation ?? "",
    companyName: data?.name_of_company ?? "",
    companyAddress: data?.company_address ?? "",
    natureOfBusiness: data?.nature_of_business ?? "",
    panCard: pan_card_image,
    maskedAadhaar: address_proof_image,
    cancelledCheque: cancelled_cheque_image,
  };
}

const CAROUSEL_IMAGES = [giftStartImg, giftFaangImg, giftDiverseImg];

const ALL_GIFT_KYC_IMAGES = [...CAROUSEL_IMAGES, giftLogoImg];

export function usePreloadGiftKycAssets() {
  useState(() => {
    ALL_GIFT_KYC_IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  });
}

function LeftPanel({ step }: { step: number }) {
  const [api, setApi] = useState<CarouselApi>();

  useEffect(() => {
    if (!api) return;
    api.scrollTo(Math.min(step - 1, CAROUSEL_IMAGES.length - 1));
  }, [api, step]);

  return (
    <div className="hidden lg:flex flex-col min-h-0 rounded-lg overflow-hidden">
      <Carousel
        opts={{ loop: false, dragFree: false }}
        setApi={setApi}
        className="flex-1 min-h-0 [&>div]:h-full"
      >
        <CarouselContent className="-ml-0 h-full">
          {CAROUSEL_IMAGES.map((img, i) => (
            <CarouselItem key={i} className="pl-0 h-full">
              <img
                src={img}
                alt=""
                draggable={false}
                className="w-full h-full object-cover select-none"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="bottom-4 top-auto left-4 translate-y-0" />
        <CarouselNext className="bottom-4 top-auto right-4 translate-y-0" />
      </Carousel>
    </div>
  );
}

export function GiftLogo({ className }: { className?: string }) {
  return (
    <img src={giftLogoImg} alt="GIFT" className={cn("h-8 w-auto", className)} />
  );
}

function StepProgressBar({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex gap-1.5">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
            i < step ? "bg-foreground" : "bg-muted-foreground/25"
          }`}
        />
      ))}
    </div>
  );
}

export interface GiftKycFormProps {
  onComplete?: (data: GiftKycFormData) => void;
  initialStep?: number;
  signingEnabled?: boolean;
  /** Preselects the client, so step 1 opens with it already chosen */
  client?: Client;
  /** Preselects the AMC, so step 1 opens with it already chosen */
  amc?: GiftKycAmc;
}

export function GiftKycForm({
  onComplete,
  initialStep = 1,
  signingEnabled,
  client,
  amc,
}: GiftKycFormProps) {
  const [step, setStep] = useState(initialStep);
  const [formData, setFormData] = useState<GiftKycFormData>(() => ({
    ...INITIAL_FORM_DATA,
    amc: amc ?? INITIAL_FORM_DATA.amc,
    selectedClient: client ?? null,
  }));

  const investmentAccountGuid =
    formData.selectedClient?.investment_account ?? "";
  const { data: details, isLoading: isLoadingDetails } = useLatestGiftInfo(
    investmentAccountGuid,
    { enabled: !!investmentAccountGuid }
  );

  // Prefill once per client. The step components read formData only when
  // they mount, so steps 2–3 wait (see isPrefilling) until this has applied.
  const [prefilledFor, setPrefilledFor] = useState<string>();
  useEffect(() => {
    if (!details || !investmentAccountGuid) return;
    if (prefilledFor === investmentAccountGuid) return;
    setFormData((f) => ({ ...f, ...getPrefillFromDetails(details) }));
    setPrefilledFor(investmentAccountGuid);
  }, [details, investmentAccountGuid, prefilledFor]);

  const isPrefilling =
    !!investmentAccountGuid &&
    (isLoadingDetails || (!!details && prefilledFor !== investmentAccountGuid));

  // The initial state above covers mount; this only matters if the caller
  // swaps the client or AMC while the form is already open.
  useEffect(() => {
    setFormData((f) =>
      (client && client !== f.selectedClient) || (amc && amc !== f.amc)
        ? {
            ...f,
            selectedClient: client ?? f.selectedClient,
            amc: amc ?? f.amc,
          }
        : f
    );
  }, [amc, client]);

  const handleNext = (data: Partial<GiftKycFormData>) => {
    // Switching clients (going back to step 1) must drop the previous client's
    // details so they aren't carried into the new client's form.
    const isNewClient =
      data.selectedClient !== undefined &&
      data.selectedClient?.id !== formData.selectedClient?.id;
    const updated = {
      ...(isNewClient ? INITIAL_FORM_DATA : formData),
      ...data,
    };
    setFormData(updated);
    if (step === TOTAL_FORM_STEPS) {
      onComplete?.(updated);
    }
    setStep((s) => s + 1);
  };

  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  const stepProps = { formData, onNext: handleNext, onBack: handleBack };
  const isFormStep = step >= 1 && step <= TOTAL_FORM_STEPS;

  const renderStep = () => {
    if ((step === 2 || step === 3) && isPrefilling) {
      return <V2Loader className="size-full" isLoading />;
    }
    switch (step) {
      case 1:
        return <ClientSelectStep {...stepProps} />;
      case 2:
        return <DocumentsStep {...stepProps} />;
      case 3:
        return <KycOccupationStep {...stepProps} />;
      case 4:
        return (
          <ReviewSignStep
            {...stepProps}
            signingEnabled={signingEnabled ?? false}
          />
        );
      case 5:
        return (
          <SuccessStep
            clientName={formData.selectedClient?.name ?? ""}
            amc={formData.amc}
            signed={formData.formSigned}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="grid lg:grid-cols-[45%_55%] size-full overflow-hidden">
      <LeftPanel step={step} />

      <div className="flex flex-col bg-background pl-10 pr-5 py-5 min-h-0 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <AppLogoLight width={36} height={36} />
            <span className="text-sm font-semibold text-foreground">
              Creso Technologies
            </span>
          </div>
          <GiftLogo />
        </div>

        {/* Progress bar */}
        {isFormStep && (
          <div className="pt-6">
            <StepProgressBar step={step} total={TOTAL_FORM_STEPS} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex flex-col pt-8 min-h-0">{renderStep()}</div>
      </div>
    </div>
  );
}
